import { Popover, PopoverButton, PopoverPanel, Transition } from "@headlessui/react";
import { Bell, Check, CheckCheck } from "lucide-react";
import { Fragment, useLayoutEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";

import {
  fetchNotifications,
  fetchUnreadCount,
  markAllRead,
  markNotificationRead,
  type NotificationInboxItem,
} from "@/features/communication/pushNotifications/api/pushNotifications.api";
import { formatDate, formatTime } from "@/lib/date";
import { markAllAsRead, setUnreadCount } from "@/screens/notifications/notificationSlice";

export function NavbarNotificationsMenu({ unreadCount }: { unreadCount: number }) {
  const dispatch = useDispatch();
  const [items, setItems] = useState<NotificationInboxItem[]>([]);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const request = useRef(0);
  const lifecycle = useRef({ active: false });
  const currentUnreadCount = useRef(unreadCount);
  useLayoutEffect(() => {
    currentUnreadCount.current = unreadCount;
  }, [unreadCount]);
  useLayoutEffect(() => {
    const session = { active: true };
    lifecycle.current = session;
    return () => {
      session.active = false;
    };
  }, []);
  const load = async (onlyUnread = unreadOnly) => {
    const session = lifecycle.current;
    const version = ++request.current;
    setLoading(true);
    setError("");
    try {
      const [result, count] = await Promise.all([
        fetchNotifications(1, 10, onlyUnread ? false : undefined),
        fetchUnreadCount(),
      ]);
      if (!session.active || version !== request.current) return;
      setItems(result.items);
      currentUnreadCount.current = count;
      dispatch(setUnreadCount(count));
    } catch {
      if (session.active && version === request.current)
        setError("Notifications could not be loaded. Please try again.");
    } finally {
      if (session.active && version === request.current) setLoading(false);
    }
  };
  const markRead = async (item?: NotificationInboxItem) => {
    const session = lifecycle.current;
    // A preview started before this write must not restore stale read state.
    request.current++;
    setLoading(false);
    setBusy(true);
    setError("");
    try {
      if (item) await markNotificationRead(item.recipientId);
      else await markAllRead();
      if (!session.active) return;
      request.current++;
      if (item) {
        setItems((previous) =>
          unreadOnly
            ? previous.filter(
                (notification) => notification.recipientId !== item.recipientId,
              )
            : previous.map((notification) =>
                notification.recipientId === item.recipientId
                  ? { ...notification, isRead: true }
                  : notification,
              ),
        );
        currentUnreadCount.current = Math.max(0, currentUnreadCount.current - 1);
        dispatch(setUnreadCount(currentUnreadCount.current));
      } else {
        currentUnreadCount.current = 0;
        dispatch(markAllAsRead());
        setItems((previous) =>
          unreadOnly
            ? []
            : previous.map((notification) => ({ ...notification, isRead: true })),
        );
      }
      await load();
    } catch {
      if (session.active)
        setError("Could not mark notifications as read. Please try again.");
    } finally {
      if (session.active) setBusy(false);
    }
  };
  return (
    <Popover className="relative">
      {({ close }) => (
        <>
          <PopoverButton
            className="shell-icon relative"
            aria-label={`Notifications, ${unreadCount} unread`}
            title="Notifications"
            onClick={() => void load()}
          >
            <Bell size={17} />
            {unreadCount > 0 && (
              <span className="absolute -right-0.5 -top-0.5 flex min-w-4 items-center justify-center rounded-full bg-blue-600 px-1 text-[10px] font-semibold leading-4 text-white">
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </PopoverButton>
          <Transition
            as={Fragment}
            enter="transition duration-200 ease-out"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition duration-150 ease-in"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <PopoverPanel
              data-workspace-overlay
              className="shell-popover fixed right-2 top-[calc(var(--platform-banner-h,0px)+48px)] z-50 w-[min(24rem,calc(100vw-1rem))] overflow-hidden sm:absolute sm:top-full sm:right-0 sm:mt-2"
              aria-label="Notifications"
            >
              <div className="flex items-center justify-between gap-2 border-b border-slate-100 px-4 py-3 dark:border-slate-800">
                <h2 className="text-sm font-semibold">Notifications</h2>
                {unreadCount > 0 && (
                  <button
                    type="button"
                    className="flex items-center gap-1 text-xs text-blue-600 disabled:opacity-50 dark:text-blue-400"
                    disabled={busy || loading}
                    onClick={() => void markRead()}
                  >
                    <CheckCheck size={14} />
                    Mark all read
                  </button>
                )}
              </div>
              <div
                className="flex gap-1 px-4 pt-3"
                role="group"
                aria-label="Notification filter"
              >
                {[false, true].map((value) => (
                  <button
                    key={String(value)}
                    type="button"
                    disabled={busy}
                    aria-pressed={unreadOnly === value}
                    onClick={() => {
                      setUnreadOnly(value);
                      void load(value);
                    }}
                    className={`rounded-md px-3 py-1.5 text-xs font-medium ${unreadOnly === value ? "bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300" : "text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800"}`}
                  >
                    {value ? "Unread" : "All"}
                  </button>
                ))}
              </div>
              <div
                className="max-h-[min(60dvh,24rem)] overflow-y-auto p-2"
                aria-busy={loading}
              >
                {error && (
                  <div
                    role="alert"
                    className="m-2 rounded-lg bg-rose-50 p-3 text-xs text-rose-700 dark:bg-rose-500/10 dark:text-rose-300"
                  >
                    {error}
                    <button
                      type="button"
                      className="ml-2 underline"
                      onClick={() => void load()}
                    >
                      Retry
                    </button>
                  </div>
                )}
                {loading ? (
                  <p role="status" className="p-6 text-center text-sm text-slate-500">
                    Loading notifications…
                  </p>
                ) : !error && items.length === 0 ? (
                  <div role="status" className="px-4 py-8 text-center">
                    <Bell size={24} className="mx-auto mb-3 text-slate-400" />
                    <p className="text-sm font-medium">
                      {unreadOnly ? "You’re all caught up" : "No notifications yet"}
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      School updates will appear here.
                    </p>
                  </div>
                ) : (
                  !loading &&
                  items.map((item) => (
                    <article
                      key={item.recipientId}
                      className={`mb-1 flex gap-3 rounded-lg p-3 ${item.isRead ? "" : "bg-blue-50/70 dark:bg-blue-500/10"}`}
                    >
                      <span
                        className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${item.isRead ? "bg-slate-300" : "bg-blue-600"}`}
                        aria-label={item.isRead ? "Read" : "Unread"}
                      />
                      <div className="min-w-0 flex-1">
                        <h3
                          className={`text-sm ${item.isRead ? "font-medium" : "font-semibold"}`}
                        >
                          {item.title}
                        </h3>
                        <p className="mt-1 break-words text-xs leading-5 text-slate-500 dark:text-slate-400">
                          {item.body}
                        </p>
                        {item.sentAtUtc && (
                          <time
                            dateTime={item.sentAtUtc}
                            className="mt-1 block text-xs text-slate-500"
                          >
                            {formatDate(item.sentAtUtc)} · {formatTime(item.sentAtUtc)}
                          </time>
                        )}
                      </div>
                      {!item.isRead && (
                        <button
                          type="button"
                          className="shell-icon shrink-0"
                          title="Mark as read"
                          aria-label={`Mark ${item.title} as read`}
                          disabled={busy}
                          onClick={() => void markRead(item)}
                        >
                          <Check size={15} />
                        </button>
                      )}
                    </article>
                  ))
                )}
              </div>
              <Link
                to="/notifications"
                onClick={() => close()}
                className="block border-t border-slate-100 p-3 text-center text-xs font-medium text-blue-600 dark:border-slate-800 dark:text-blue-400"
              >
                View notification inbox
              </Link>
            </PopoverPanel>
          </Transition>
        </>
      )}
    </Popover>
  );
}
